import { ReactElement, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { hasAuthParams } from "react-oidc-context";
import { useAtomValue, useSetAtom } from "jotai";
import { fetchUserPreferencesAtom,userPreferencesAtom } from "atoms";
import { fetchQuotaUsageAtom } from "atoms/quota";
import BrowserDisclaimer from "shared/components/BrowserDisclaimer";
import Homepage from "features/Homepage";
import SSML from "features/SSML";
import TTSHomepage from "features/TTS";
import Navbar from "shared/containers/Navbar";
import { useOIDCLogin } from "shared/hooks/useOIDCLogin";
import MarkdownRenderer from "shared/containers/MarkdownRenderer";
import { RELEASE_NOTES_URL } from "shared/constants";
import { PATHS } from "shared/constants/paths";
import AccessDeniedNotification from "shared/components/AccessDeniedNotification";
import {
  Copyright,
  ThirdPartyNotices,
} from "@web-applications/allinga-web-components";
import CheatSheet from "features/SSML/CheatSheet";

export default function AppRoutes(): ReactElement {
  const { auth, hasAllingaStageTTSAccess } = useOIDCLogin();
  const fetchUserPreferences = useSetAtom(fetchUserPreferencesAtom);
  const authenticated = auth.isAuthenticated && hasAllingaStageTTSAccess;
  const fetchQuota = useSetAtom(fetchQuotaUsageAtom);
  // get auto-login params from URL
  const urlParams = new URLSearchParams(window.location.search);
  const hasAutoLoginParams = urlParams.get("auto-login") === "true";
  const userPreferences = useAtomValue(userPreferencesAtom);
  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) fetchUserPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoading]);

  useEffect(() => {
  if (!auth.isLoading && auth.isAuthenticated && userPreferences._id) {
    fetchQuota();
  }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [userPreferences._id]);

  useEffect(() => {
    const hasParams = hasAuthParams();
    const isAuth = auth.isAuthenticated;

    if (!hasParams && !isAuth) {
      if (hasAutoLoginParams) {
        auth.signinRedirect();
      } else {
        auth.signinSilent();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  // wait for the authentication to be loaded, true or false
  if (auth.isLoading || auth.activeNavigator) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (auth.isAuthenticated && !hasAllingaStageTTSAccess)
    return <AccessDeniedNotification />;

  return (
    <>
      {!auth.isLoading && <Navbar />}
      <BrowserDisclaimer />
      <Routes>
        <Route
          path={PATHS.root}
          element={authenticated ? <TTSHomepage /> : <Homepage />}
        />
        <Route
          path={PATHS.releaseNotes}
          element={
            authenticated ? (
              <MarkdownRenderer url={RELEASE_NOTES_URL} />
            ) : (
              <Homepage />
            )
          }
        />
        <Route
          path={PATHS.ssmlGuide}
          element={authenticated ? <SSML /> : <Homepage />}
        />
        <Route path={PATHS.ssmlCheatSheet} element={<CheatSheet />} />
        <Route path={PATHS.thirdPartyNotices} element={<ThirdPartyNotices />} />
        <Route path={PATHS.copyright} element={<Copyright />} />

        {/* Redirect any URL to root */}
        <Route path="*" element={<Navigate to={PATHS.root} />} />
      </Routes>
    </>
  );
}
